import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { BehaviorSubject, concat, EMPTY, from, Observable, Subscription } from 'rxjs';
import { concatMap, filter, finalize, switchMap, take, tap } from 'rxjs/operators';
import { AppConfig } from '../../../app.settings';

import { AppConstants } from '../../../app.constants';
import { PlaylistItemDTO } from '../../models/view-models/playlist-item-models/playlist-item-dto.model';
import { ILauncherFeature } from '../../interfaces/launcher-feature.interface';
import { PlaylistItemType } from '../../models/enums/playlist-item-type.enum';
import { PlaylistDTO } from '../../models/view-models/playlist-models/playlist-dto.model';
import { RssTickerConfigs } from '../../models/entities/rss-ticker.type';
import { PlaylistItemLinkModel } from '../../models/view-models/playlist-item-models/playlist-item-link.model';
import { PlaylistItemBorderSettings } from '../../models/view-models/playlist-item-models/playlist-item-border-settings.model';
import { Launcher } from '../../models/entities/launcher.type';
import { FilesService } from './files.service';
import { ModalProcessingStatusComponent } from '../../../shared-module/components/_modals/modal-processing-status/modal-processing-status.component';
import { PlaylistDialogMessagesEnum } from '../../enums/toastr-messages/playlist-dialog-messages.enum';
import { DeleteDialogService } from '../dialog-services/delete-dialog.service';

@Injectable()
export class PlaylistItemsService {

  endpoint: string;
  mp4Items: PlaylistItemDTO[] = [];
  convertingItem = new BehaviorSubject<PlaylistItemDTO>(null);

  constructor(
    public http: HttpClient,
    public config: AppConfig,
    private filesService: FilesService,
    private dialog: MatDialog,
    private dialogService: DeleteDialogService
  ) {
    this.endpoint = this.config.getConfig('api') + 'Application';
   }

   getPlaylistItem(id: number): Observable<PlaylistItemDTO> {
    return this.http.get<PlaylistItemDTO>(this.endpoint + '/' + id, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }

  relaunchClient(physicalAddresses: string[]): Observable<void> {
    return this.http.post<void>(`${this.endpoint}/relaunch-mawiclient`, physicalAddresses);
  }

  duplicateItem(item: PlaylistItemDTO, launcherFeatures: ILauncherFeature[]): PlaylistItemDTO {
    const { Id, ...newItem } = item;
    const { Id: rssId, PlaylistItemId, ...configs } = { ...item.RssConfigs };
    const { Id: borderId, PlaylistItemId: itemId, ...borderSettings } = { ...item.BordersSettings };

    const copiedItemLinks: PlaylistItemLinkModel[] = [];
    item.PlaylistItemLinks.forEach(source => {
      const { Id, ...model } = structuredClone(source);
      copiedItemLinks.push(model as PlaylistItemLinkModel);
    });

    return {
      ...newItem as PlaylistItemDTO,
      RssConfigs: item.RssConfigs ? configs as RssTickerConfigs : null,
      BordersSettings: item.BordersSettings ? borderSettings as PlaylistItemBorderSettings : null,
      PlaylistItemLinks: copiedItemLinks,
      launcherFeatures: launcherFeatures.map(
        launcherFeature => ({
          ...launcherFeature,
          Id: 0,
          PlaylistItemId: 0
        })
      )
    };
  }

  processFiles(addedItems: PlaylistItemDTO[], playlist: PlaylistDTO) {
    const pptItems = addedItems.filter(item =>
      this.filesService.getFileType(item)?.includes(AppConstants.PPT_FILE_TYPE));

    concat(
      pptItems.length
        ? this.dialogService.show(PlaylistDialogMessagesEnum.AndroidPlaylistPPTConvertingMessage, false).pipe(
            filter(isToContinue => !!isToContinue),
            switchMap(() => from(pptItems).pipe(
              concatMap(item => this.convertPPTFile(item, playlist.PlaylistItems))
            ))
          )
        : EMPTY,
      from(addedItems).pipe(
        filter(item => this.filesService.shouldConvertPortraitToLandscape(item)),
        concatMap(item => this.convertMP4ToLandscape(item))
      )
    ).subscribe();
  }

  transformItems(playlist: PlaylistDTO): PlaylistItemDTO[] {
    const playlistItems: PlaylistItemDTO[] = [];
    const pptItems: PlaylistItemDTO[] = [];

    playlist.PlaylistItems
      .filter(item => item.PlaylistItemType !== PlaylistItemType.LocalFile
        && item.PlaylistItemType !== PlaylistItemType.Foreground)
      .forEach(item => {
        if (playlist.IsAndroid && this.filesService.getFileType(item)?.includes(AppConstants.PPT_FILE_TYPE)) {
          pptItems.push(item);
        }
        else {
          const playlistItem = new PlaylistItemDTO();
          playlistItem.Duration = item.Duration;
          playlistItem.ExactVideoDuration = item.ExactVideoDuration;
          playlistItem.PlaylistItemType = item.PlaylistItemType;
          playlistItem.PlaylistItemLinks = [item.PlaylistItemLinks[0]];

          if (playlist.Id) {
            playlistItem.Id = item.Id;
            playlistItem.PlaylistId = playlist.Id;
          }

          if (playlistItem.PlaylistItemType === PlaylistItemType.Url) {
            playlistItem.PlaylistItemTitle = playlistItem.PlaylistItemLinks[0].Link || AppConstants.EMPTY_URL_ITEM_TITLE;
          }
          else if (playlistItem.PlaylistItemType === PlaylistItemType.RecentFile) {
            playlistItem.PlaylistFileName = item.PlaylistFileName;
            playlistItem.PlaylistItemTitle = item.PlaylistFileName;

            if (!playlist.IsAndroid && AppConstants.PPTX_TRANSFORMED_SLIDE_REGULAR.test(playlistItem.PlaylistFileName)) {
              this.setMawChromeLauncher(playlistItem);
            }
          }

          playlistItems.push(playlistItem);
        }
      });

    if (playlist.IsAndroid) {
      concat(
        from(pptItems).pipe(
          concatMap(item => this.convertPPTFile(item, playlistItems))
        ),
        // from(playlistItems).pipe(
        //   filter(item => this.filesService.shouldConvertPortraitToLandscape(item)),
        //   concatMap(item => this.convertMP4ToLandscape(item))
        // )
      ).subscribe();
    }

    if (!playlist.IsAndroid && this.mp4Items.length) {
      this.convertMP4FilesToWebm(this.mp4Items);
    }

    return playlistItems;
  }

  playlistHasForegroundRssItem(playlist: PlaylistDTO): boolean {
    return playlist.IsAndroid &&
      playlist.PlaylistItems.some(item =>
        item.PlaylistItemType === PlaylistItemType.Foreground &&
        !!item.RssConfigs
    );
  }

  playlistHasAllForegroundItemTypes(playlist: PlaylistDTO): boolean {
    const foregroundItems = playlist.PlaylistItems.filter(item => item.PlaylistItemType === PlaylistItemType.Foreground);

    return foregroundItems.length > 0 &&
      foregroundItems.some(item => !!item.RssConfigs) &&
      foregroundItems.some(item => !!item.TimerConfig);
  }

  validateRss(playlist: PlaylistDTO) {
    if (this.playlistHasForegroundRssItem(playlist)) {
      playlist.PlaylistItems
        .filter(item => item.PlaylistItemType !== PlaylistItemType.Foreground)
        .forEach(item => item.RssConfigs = null);
    }
  }

  private convertMP4FilesToWebm(itemsToConvert: PlaylistItemDTO[]) {
    let convertingRequest: Subscription;
    const fileIds = itemsToConvert.map(item => item.PlaylistItemLinks[0].FileId);

    const modal = this.dialog.open(ModalProcessingStatusComponent, {
      disableClose: true,
      autoFocus: false
    });

    modal.componentInstance.header = PlaylistDialogMessagesEnum.PlaylistMP4ToWebmConvertingTitle;
    modal.afterClosed().pipe(take(1)).subscribe(() => {
      convertingRequest?.unsubscribe();
    });

    convertingRequest = this.filesService.convertMP4ToWebm(fileIds).pipe(
      tap(files => {
        itemsToConvert.forEach((item, i) => {
          item.PlaylistFileName = files[i].FileName;
          item.PlaylistItemTitle = files[i].FileName;
          item.PlaylistItemLinks[0].FileId = files[i].Id;
          item.PlaylistItemLinks[0].File = files[i];
        });
      }),
      finalize(() => modal.close()),
      take(1)
    ).subscribe();
  }

  private convertPPTFile(itemToConvert: PlaylistItemDTO, playlistItems: PlaylistItemDTO[]) {
    this.convertingItem.next(itemToConvert);

    return this.filesService.convertPresentationToImages(itemToConvert.PlaylistItemLinks[0].FileId, false).pipe(
      tap(files => {
        this.filesService.generateItem(files, playlistItems, false);
        const itemToRemoveIndex = playlistItems.findIndex(item =>
          item.PlaylistItemLinks[0].FileId === itemToConvert.PlaylistItemLinks[0].FileId);

        if (itemToRemoveIndex > -1) {
          playlistItems.splice(itemToRemoveIndex, 1);
        }
        this.convertingItem.next(null);
      })
    );
  }

  private convertMP4ToLandscape(itemToConvert: PlaylistItemDTO) {
    this.convertingItem.next(itemToConvert);

    return this.filesService.convertPortraitToLandscape([itemToConvert.PlaylistItemLinks[0].FileId]).pipe(
      tap(file => itemToConvert.PlaylistItemLinks[0].File = file[0]),
      finalize(() => this.convertingItem.next(null))
    );
  }

  private setMawChromeLauncher(item: PlaylistItemDTO) {
    item.IsMuted = true;
    item.IsContentStretched = true;
    item.LauncherId = AppConstants.CHROME_LAUNCHER_ID;
    item.Launcher = { ExecutableName: AppConstants.CHROME_LAUNCHER_EXECUTABLE_NAME } as Launcher;

    if (AppConstants.MP4_FILE_TYPE.includes(this.filesService.getFileType(item))) {
      this.mp4Items.push(item);
    }
  }
}
